import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const qc = useQueryClient();
  const accessKey = useRef("");
  const clearPrivateCache = useCallback(data => {
    const key = JSON.stringify([data?.id, data?.role, data?.approval_status, data?.supervision, data?.masar?.profile_id, data?.work_type, data?.profile_change_allowed]);
    if (key !== accessKey.current) { qc.clear(); accessKey.current = key; }
  }, [qc]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      clearPrivateCache(data);
      setUser(data);
      return data;
    } catch {
      clearPrivateCache(null);
      setUser(null);
      localStorage.removeItem("hrm_token");
      return null;
    }
  }, [clearPrivateCache]);

  useEffect(() => {
    const token = localStorage.getItem("hrm_token");
    if (!token) { setLoading(false); return; }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, email: identifier, password });
    localStorage.setItem("hrm_token", data.token);
    clearPrivateCache(data.user);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, password, work_type) => {
    const { data } = await api.post("/auth/register", { name, password, work_type });
    localStorage.setItem("hrm_token", data.token);
    clearPrivateCache(data.user);
    setUser(data.user);
    return data;
  };

  const logout = () => {
    localStorage.removeItem("hrm_token");
    clearPrivateCache(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
